import {
	AlertTriangle,
	Bell,
	BellRing,
	CircleAlert,
	Image,
	Info,
	MoveRight,
	PlayCircle,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import {
	ACTIVE_NOTICES_PARAMS,
	useNotices,
} from '@/api/queries/notices-queries'
import {
	useMarkNotificationAsRead,
	useNotifications,
} from '@/api/queries/notifications-queries'
import { Button } from '@/components/ui/button'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/components/ui/tooltip'
import { ROUTES } from '@/constants/routes'
import { cn } from '@/lib/utils'
import type {
	NoticeMediaKind,
	NoticeType,
	NoticeWithReadStatus,
} from '@/types/notice'
import type { SystemNotification } from '@/types/notification'
import { getNoticeMediaKind } from '@/utils/notice-media'

type InboxItem = {
	id: string
	title: string
	message: string
	type?: NoticeType
	iconType: NoticeType
	readAt?: string | null
	kind: 'notice' | 'notification'
	mediaKind?: NoticeMediaKind
}

const TYPE_ICON: Record<NoticeType, typeof Info> = {
	info: Info,
	warning: AlertTriangle,
	danger: CircleAlert,
}

const TYPE_ICON_CLASS: Record<NoticeType, string> = {
	info: 'text-kaefer-sapphire-blue',
	warning: 'text-amber-500',
	danger: 'text-red-500',
}

export const NoticeBell = () => {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const location = useLocation()

	const { data: noticesData } = useNotices(ACTIVE_NOTICES_PARAMS)
	const { data: notificationsData } = useNotifications()
	const markNotificationAsRead = useMarkNotificationAsRead()

	const [open, setOpen] = useState(false)
	const [tooltipOpen, setTooltipOpen] = useState(false)
	const [notificationItems, setNotificationItems] = useState<
		SystemNotification[]
	>([])

	useEffect(() => {
		setNotificationItems(notificationsData?.items ?? [])
	}, [notificationsData?.items])

	const noticeItems = useMemo<InboxItem[]>(() => {
		return (noticesData?.items ?? [])
			.filter((notice: NoticeWithReadStatus) => !notice.isRead)
			.map((notice) => ({
				id: notice.id,
				title: notice.title,
				message: notice.message,
				type: notice.type,
				iconType: notice.type,
				readAt: notice.isRead ? notice.createdAt : null,
				kind: 'notice',
				mediaKind: getNoticeMediaKind(notice.media),
			}))
	}, [noticesData?.items])

	const unreadNotifications = useMemo<InboxItem[]>(() => {
		return notificationItems
			.filter((notification) => !notification.readAt)
			.map((notification) => ({
				id: notification.id,
				title: notification.title,
				message: notification.message,
				type: notification.type,
				iconType: notification.type ?? 'info',
				readAt: notification.readAt,
				kind: 'notification',
			}))
	}, [notificationItems])

	const unreadCount = noticeItems.length + unreadNotifications.length

	useEffect(() => {
		if (location.pathname === ROUTES.DASHBOARD.NOTICES) return
		if (sessionStorage.getItem('ssma-inbox-tooltip-shown')) return
		if (unreadCount === 0) return

		setTooltipOpen(true)
		sessionStorage.setItem('ssma-inbox-tooltip-shown', '1')
		const timer = setTimeout(() => setTooltipOpen(false), 3500)
		return () => clearTimeout(timer)
	}, [location.pathname, unreadCount])

	const handleNotificationClick = async (item: InboxItem) => {
		if (item.kind !== 'notification' || item.readAt) return

		const previousItems = notificationItems
		const optimisticReadAt = new Date().toISOString()
		setNotificationItems((current) =>
			current.map((currentItem) =>
				currentItem.id === item.id
					? { ...currentItem, readAt: optimisticReadAt }
					: currentItem,
			),
		)

		try {
			await markNotificationAsRead.mutateAsync(item.id)
		} catch (_error) {
			setNotificationItems(previousItems)
		}
	}

	const renderSection = (
		label: string,
		items: InboxItem[],
		kind: InboxItem['kind'],
	) => {
		if (items.length === 0) return null

		return (
			<div className="border-b last:border-b-0">
				<div className="flex items-center justify-between px-4 py-2">
					<span className="text-xs font-semibold uppercase tracking-wide text-kaefer-medium-grey">
						{label}
					</span>
					<span className="rounded-full bg-kaefer-light-silver px-2 py-0.5 text-[11px] font-semibold text-kaefer-dark-grey">
						{items.length}
					</span>
				</div>
				<ul className="divide-y">
					{items.map((item) => {
						const iconType = item.iconType ?? 'info'
						const Icon = TYPE_ICON[iconType]
						const content = (
							<>
								<Icon
									className={cn(
										'mt-0.5 size-4 shrink-0',
										TYPE_ICON_CLASS[iconType],
									)}
								/>
								<div className="min-w-0 flex-1">
									<p className="flex items-center gap-1.5 text-sm font-medium text-kaefer-dark-grey">
										<span className="truncate">{item.title}</span>
										{item.mediaKind === 'video' && (
											<PlayCircle className="size-3.5 shrink-0 text-muted-foreground" />
										)}
										{item.mediaKind === 'image' && (
											<Image className="size-3.5 shrink-0 text-muted-foreground" />
										)}
									</p>
									<p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
										{item.message}
									</p>
								</div>
							</>
						)

						const handleClick = () => {
							if (kind === 'notification') {
								void handleNotificationClick(item)
								navigate(ROUTES.INSPECTIONS.ACTIONS_IN_PROGRESS)
								return
							}
							setOpen(false)
							navigate(ROUTES.DASHBOARD.NOTICE_DETAIL.replace(':id', item.id))
						}

						return (
							<li key={item.id}>
								<button
									type="button"
									className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/40 cursor-pointer"
									onClick={handleClick}
								>
									{content}
								</button>
							</li>
						)
					})}
				</ul>
			</div>
		)
	}

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
				<TooltipTrigger asChild>
					<PopoverTrigger asChild>
						<div className="relative">
							<Button
								variant="ghost"
								size="icon"
								aria-label={t('header.inbox.bellLabel')}
								className="size-10 p-0 text-kaefer-silver hover:bg-white/10 hover:text-kaefer-silver md:size-8"
							>
								{unreadCount >= 1 ? (
									<BellRing className="size-5 animate-[bell-ring_2.5s_ease-in-out_infinite] origin-top" />
								) : (
									<Bell className="size-5" />
								)}
							</Button>
							{unreadCount > 0 && (
								<span className="absolute right-1 top-1 flex min-w-4 translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-destructive px-1 text-xs text-white">
									{unreadCount > 99 ? '99+' : unreadCount}
								</span>
							)}
						</div>
					</PopoverTrigger>
				</TooltipTrigger>

				<TooltipContent sideOffset={6}>
					{unreadCount === 0
						? t('header.inbox.tooltip.none')
						: unreadCount === 1
							? t('header.inbox.tooltip.one')
							: t('header.inbox.tooltip.other', { count: unreadCount })}
				</TooltipContent>
			</Tooltip>

			<PopoverContent
				align="end"
				sideOffset={8}
				className="w-96 border-kaefer-light-silver p-0 shadow-lg"
			>
				<div className="flex items-center justify-between border-b px-4 py-3">
					<span className="text-sm font-semibold text-kaefer-dark-grey">
						{t('header.inbox.title')}
					</span>
					{unreadCount > 0 && (
						<span className="rounded-full bg-kaefer-red px-2 py-0.5 text-[11px] font-bold text-white">
							{unreadCount} {t('header.inbox.unread')}
						</span>
					)}
				</div>

				<div className="max-h-[420px] overflow-y-auto">
					{renderSection(t('notices.title'), noticeItems, 'notice')}
					{renderSection(
						t('notifications.title'),
						unreadNotifications,
						'notification',
					)}
					{unreadCount === 0 && (
						<div className="flex flex-col items-center justify-center gap-2 py-8 text-muted-foreground">
							<Bell className="size-8 opacity-30" />
							<p className="text-sm">{t('header.inbox.empty')}</p>
						</div>
					)}
				</div>

				<div className="border-t px-4 py-2.5">
					<Button
						variant="link"
						className="h-auto w-full gap-1.5 p-0 text-sm text-kaefer-sapphire-blue"
						onClick={() => {
							setOpen(false)
							navigate(ROUTES.DASHBOARD.NOTICES)
						}}
					>
						{t('notices.banner.viewAll')}
						<MoveRight className="size-3.5" />
					</Button>
				</div>
			</PopoverContent>
		</Popover>
	)
}
